// src/components/HeroSection.js
// ──────────────────────────────────────────────────────────────────────────
// Full-screen hero slider. Each slide slowly zooms (Ken Burns), auto-advances
// with a soft crossfade, and can be triggered manually via arrows or dots.
// Large imagery, small refined text. Swap SLIDES images for real Mbala photos.
// ──────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useSiteContent } from '../context/SiteContentContext';
import theme from '../theme/theme';

const DEFAULT_SLIDES = [
  {
    image: 'https://images.pexels.com/photos/3760930/pexels-photo-3760930.jpeg?auto=compress&cs=tinysrgb&w=2400',
    kicker: 'Mbala Training Institute',
    title: 'Building you from scratch.',
    sub: 'Hands-on skills for youths, women and the community — so you can earn a living with dignity.',
  },
  {
    image: 'https://images.pexels.com/photos/14621560/pexels-photo-14621560.jpeg?auto=compress&cs=tinysrgb&w=2400',
    kicker: '80% practical',
    title: 'Skills that pay.',
    sub: 'Food production, agriculture, construction trades and ICT — learn by doing, not just reading.',
  },
  {
    image: 'https://images.pexels.com/photos/34411687/pexels-photo-34411687.jpeg?auto=compress&cs=tinysrgb&w=2400',
    kicker: 'Making your dreams come true',
    title: 'From training to enterprise.',
    sub: 'We don’t stop at a certificate — we support you into work and into business.',
  },
];

const AUTOPLAY_MS = 7000;   // time each slide is shown
const ZOOM_SECONDS = 11;    // Ken Burns zoom duration (higher = slower/calmer)
const ZOOM_TO = 1.10;       // how far it zooms in (1.10 = +10%)

const Stage = styled.section`
  position: relative;
  width: 100%;
  height: calc(100vh - 108px);   /* full-screen below the two-tier header */
  min-height: 520px;
  overflow: hidden;
  background: ${theme.color.blueDeep};
  @media (max-width: 980px) { height: calc(100vh - 70px); }
`;

const Slide = styled(motion.div)`
  position: absolute; inset: 0;
  background-image: url(${(p) => p.$src});
  background-size: cover;
  background-position: center;
  will-change: transform, opacity;
`;

const Scrim = styled.div`
  position: absolute; inset: 0;
  background: ${theme.gradient.heroScrim};
  z-index: 2;
`;

const Content = styled.div`
  position: absolute;
  left: 0; bottom: 0;
  z-index: 3;
  width: 100%;
  padding: 0 ${theme.layout.gutter} 10vh;
  color: #fff;
`;

const Kick = styled(motion.span)`
  display: inline-flex; align-items: center; gap: 10px;
  font-size: 0.72rem; font-weight: 600; letter-spacing: 0.22em; text-transform: uppercase;
  color: #fff;
  &::before { content: ''; width: 34px; height: 2px; background: ${theme.gradient.rule}; }
`;

const Title = styled(motion.h1)`
  font-weight: 400;                 /* light = elegant at large size */
  font-size: clamp(2.6rem, 7vw, 6rem);
  max-width: 16ch;
  margin: 1rem 0 0.8rem;
  color: #fff;
`;

const Sub = styled(motion.p)`
  font-size: 0.98rem;               /* small refined paragraph */
  font-weight: 300;
  max-width: 42ch;
  color: rgba(255,255,255,0.85);
  margin: 0 0 1.8rem;
`;

const Actions = styled.div`
  display: flex; gap: 0.9rem; flex-wrap: wrap;
`;

const Primary = styled(Link)`
  background: #fff; color: ${theme.color.accent};
  font-size: 0.82rem; font-weight: 700; letter-spacing: 0.02em;
  padding: 13px 26px; border-radius: ${theme.layout.radius};
  &:hover { background: ${theme.color.lemon}; color: ${theme.color.onLemon}; }
`;

const Ghost = styled(Link)`
  border: 1px solid rgba(255,255,255,0.6); color: #fff;
  font-size: 0.82rem; font-weight: 700;
  padding: 12px 24px; border-radius: ${theme.layout.radius};
  &:hover { background: rgba(255,255,255,0.12); }
`;

const Arrows = styled.div`
  position: absolute; z-index: 4;
  right: ${theme.layout.gutter}; bottom: 10vh;
  display: flex; gap: 10px;
  @media (max-width: 760px) { display: none; }
  button {
    width: 48px; height: 48px; border-radius: 50%;
    background: rgba(255,255,255,0.12);
    border: 1px solid rgba(255,255,255,0.4);
    color: #fff; cursor: pointer; font-size: 1rem;
    transition: background 0.25s ${theme.ease};
    &:hover { background: rgba(255,255,255,0.25); }
  }
`;

const Dots = styled.div`
  position: absolute; z-index: 4;
  left: ${theme.layout.gutter}; bottom: 5vh;
  display: flex; gap: 10px;
  button {
    width: 30px; height: 3px; border: none; cursor: pointer; padding: 0;
    background: rgba(255,255,255,0.35);
    transition: background 0.3s ${theme.ease};
  }
  button.active { background: ${theme.color.lemon}; }
`;

const HeroSection = () => {
  const editable = useSiteContent('heroSlides');
  const SLIDES = (Array.isArray(editable) && editable.length) ? editable : DEFAULT_SLIDES;
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = SLIDES.length;

  const go = useCallback((n) => setIndex(((n % count) + count) % count), [count]);

  useEffect(() => {
    if (paused) return undefined;
    const t = setTimeout(() => go(index + 1), AUTOPLAY_MS);
    return () => clearTimeout(t);
  }, [index, paused, go]);

  const slide = SLIDES[index] || DEFAULT_SLIDES[0];

  return (
    <Stage onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <AnimatePresence>
        <Slide
          key={index}
          $src={slide.image}
          initial={{ opacity: 0, scale: 1.0 }}
          animate={{ opacity: 1, scale: ZOOM_TO }}
          exit={{ opacity: 0 }}
          transition={{ opacity: { duration: 1.4, ease: 'easeInOut' }, scale: { duration: ZOOM_SECONDS, ease: 'linear' } }}
        />
      </AnimatePresence>

      <Scrim />

      <Content>
        <AnimatePresence mode="wait">
          <motion.div key={index}
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.6, ease: theme.ease }}>
            <Kick>{slide.kicker}</Kick>
            <Title>{slide.title}</Title>
            <Sub>{slide.sub}</Sub>
          </motion.div>
        </AnimatePresence>
        <Actions>
          <Primary to="/programs">Explore programmes</Primary>
          <Ghost to="/admissions">How to apply</Ghost>
        </Actions>
      </Content>

      <Arrows>
        <button onClick={() => go(index - 1)} aria-label="Previous">‹</button>
        <button onClick={() => go(index + 1)} aria-label="Next">›</button>
      </Arrows>

      <Dots>
        {SLIDES.map((_, i) => (
          <button key={i} className={i === index ? 'active' : ''} onClick={() => go(i)} aria-label={`Slide ${i + 1}`} />
        ))}
      </Dots>
    </Stage>
  );
};

export default HeroSection;
